import { useCallback, useState } from "react";

const STORAGE_PREFIX = "bandwith-transpose:";

// Cap at just under an octave; a full octave is musically identical and a
// runaway value would produce nonsensical chord names.
const MAX_SEMITONES = 11;
const MIN_SEMITONES = -11;

function clamp(value: number): number {
  return Math.max(MIN_SEMITONES, Math.min(MAX_SEMITONES, value));
}

function readStored(songId: string): number {
  const stored = localStorage.getItem(STORAGE_PREFIX + songId);
  const parsed = stored === null ? Number.NaN : Number(stored);
  return Number.isInteger(parsed) ? clamp(parsed) : 0;
}

/**
 * Hook for managing per-song chord transposition.
 *
 * Transposition is a property of a particular song (it tracks the key a band
 * sings it in), so the offset is persisted per song id rather than globally.
 * The two song views (detail and perform) therefore stay in sync.
 *
 * The value is read synchronously from localStorage so a saved offset applies
 * on the first render — no flash from 0 to the stored value. Safe because the
 * SPA is client-rendered (no SSR hydration to mismatch). The router reuses this
 * component across songs, so we also re-read during render when songId changes
 * rather than via an effect (which would flash on every navigation).
 */
export function useTranspose(songId: string) {
  const [transpose, setTranspose] = useState(() => readStored(songId));
  const [loadedSongId, setLoadedSongId] = useState(songId);

  if (songId !== loadedSongId) {
    setLoadedSongId(songId);
    setTranspose(readStored(songId));
  }

  const handleChange = useCallback(
    (next: number) => {
      const clamped = clamp(next);
      setTranspose(clamped);
      localStorage.setItem(STORAGE_PREFIX + songId, String(clamped));
    },
    [songId],
  );

  return { transpose, handleChange };
}
